type Entries<T> = {
    [K in keyof T]: [K, T[K]];
}[keyof T][];

export function typedKeys<T extends object>(obj: T): Array<keyof T> {
    return Object.keys(obj) as Array<keyof typeof obj>;
}

export function typedEntries<T extends object>(object: T): Entries<T> {
    return Object.entries(object) as Entries<T>;
}

export function typedValues<T extends object>(obj: T): Array<T[keyof T]> {
    return Object.values(obj) as Array<T[keyof typeof obj]>;
}

export function filterValues<T extends object>(
    obj: T,
    check: (v: unknown) => boolean
): Partial<T> {
    return typedKeys(obj)
        .filter((key) => check(obj[key]))
        .reduce((subset, key) => {
            subset[key] = obj[key];
            return subset;
        }, {} as Partial<T>);
}

export const groupByWithTotal = <T, K extends keyof any>(
    list: T[],
    getKey: (item: T) => K,
    getTotal: (item: T) => number
) =>
    list.reduce(
        (previous, currentItem) => {
            const key = getKey(currentItem);
            const total = getTotal(currentItem);
            if (!previous[key]) previous[key] = { items: [], total: 0 };
            previous[key].items.push(currentItem);
            previous[key].total += total;

            return previous;
        },
        {} as Record<K, { items: T[]; total: number }>
    );

export const omitUndefinedProperties = <T extends object>(obj: T): T =>
    Object.fromEntries(
        Object.entries(obj).filter(([, v]) => v !== undefined)
    ) as T;

export function mapObject<T, R>(
    obj: Record<string, T>,
    fn: (value: T, key: string) => R
): Record<string, R> {
    return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, fn(value, key)])
    );
}
